import {
  CREATED_BY_CLOUD_SHELL,
  MANAGED_BY_TERRAFORM,
  P0_TAG_NAME,
} from "@p0security/integrations-aws-shared/constants";
import {
  buildGcpP0TrustPolicy,
  envSuffixFor,
} from "@p0security/integrations-aws-shared/policy/shared";
import type { DevEnv } from "@p0security/shared/types/environment";

import { type AuditBucket, bucketArn } from "./bucket.ts";

export const s3AuditRoleName = (context: DevEnv): string =>
  `P0RoleIamS3Writer${envSuffixFor(context)}`;

export const s3AuditInlinePolicyName = (context: DevEnv): string =>
  `${s3AuditRoleName(context)}Policy`;

type BucketArgs = Pick<AuditBucket, "accountId" | "bucket" | "partition">;

/** P0's only permissions: verify its own role and write new objects */
export const s3AuditInlinePolicy = (context: DevEnv, args: BucketArgs) => {
  const { accountId, bucket, partition } = args;
  const roleArn = `arn:${partition}:iam::${accountId}:role/${s3AuditRoleName(context)}`;
  return {
    Version: "2012-10-17",
    Statement: [
      {
        Sid: "P0CanVerifyOwnRole",
        Effect: "Allow",
        Action: ["iam:GetRole", "iam:GetRolePolicy"],
        Resource: roleArn,
      },
      {
        Sid: "P0CanWriteAuditFiles",
        Effect: "Allow",
        Action: "s3:PutObject",
        Resource: `${bucketArn(partition, bucket)}/*`,
      },
    ],
  };
};

type InstallArgs = BucketArgs & { gcloudServiceAccountId: string };

export const s3AuditInstallCommands = (
  context: DevEnv,
  args: InstallArgs
): string => {
  const roleName = s3AuditRoleName(context);
  const trustPolicy = buildGcpP0TrustPolicy(args.gcloudServiceAccountId);
  const policy = s3AuditInlinePolicy(context, args);
  return `export CALLER=$(aws sts get-caller-identity --query Arn --output text) && \\
(aws iam get-role --role-name "${roleName}" > /dev/null 2>&1 || \\
aws iam create-role \\
  --tags Key="${P0_TAG_NAME}",Value="${CREATED_BY_CLOUD_SHELL} by \${CALLER}" \\
  --role-name "${roleName}" \\
  --assume-role-policy-document '${JSON.stringify(trustPolicy, undefined, 2)}') \\
  && \\
aws iam put-role-policy \\
  --role-name "${roleName}" \\
  --policy-name "${s3AuditInlinePolicyName(context)}" \\
  --policy-document '${JSON.stringify(policy, undefined, 2)}'`;
};

export const s3AuditInstallTerraform = (
  context: DevEnv,
  args: InstallArgs
): string => {
  const roleName = s3AuditRoleName(context);
  const trustPolicy = buildGcpP0TrustPolicy(args.gcloudServiceAccountId);
  const policy = s3AuditInlinePolicy(context, args);
  return `resource "aws_iam_role" "p0_aws_s3_audit_writer_role" {
  name = "${roleName}"

  assume_role_policy = <<EOF
${JSON.stringify(trustPolicy, undefined, 2)}
EOF

  tags = {
    ${P0_TAG_NAME} = "${MANAGED_BY_TERRAFORM}"
  }
}

resource "aws_iam_role_policy" "p0_aws_s3_audit_writer_policy" {
  name = "${s3AuditInlinePolicyName(context)}"
  role = aws_iam_role.p0_aws_s3_audit_writer_role.name

  policy = <<EOF
${JSON.stringify(policy, undefined, 2)}
EOF
}`;
};
